import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery } from 'react-query'
import { Button } from 'antd'
import { FormattedMessage, useIntl } from 'react-intl'

import {
  financeOnboardingSnoozedSelector,
  financeOnboardingStepSelector,
  financeOnboardingValueSelector,
  isFinanceOnboardingEnabledSelector,
  loadedInitSelector,
  mobileViewSelector,
  selectedEndpointSelector,
  userSelector,
} from 'app-redux/selectors'
import {
  FINANCE_ONBOARDING_BE,
  FINANCE_ONBOARDING_BE_ORDER,
  FINANCE_ONBOARDING_STEP as S,
  isStepInFinanceOnboardingMilestone,
  resetFinanceOnboarding,
  setFinanceOnboardingStep,
  snoozeFinanceOnboarding,
} from 'app-redux/financeOnboardingSlice'
import { editOption } from 'app-redux/options/actions'
import { useApp } from 'components/AppProvider'
import { getFinanceInvoices } from 'utils/apis/financeInvoice'

import { STEPS } from './steps'
import { clearSnooze, clearStep, loadSnooze, loadStep, saveSnooze, saveStep } from './storage'
import Spotlight from './Spotlight'
import Welcome from './Welcome'
import ProgressRail from './ProgressRail'
import FloatingCard from './FloatingCard'
import FinishModal from './FinishModal'
import SnoozeButton from './SnoozeButton'
import styles from './financeOnboarding.module.scss'

const POLL_MS = 300
const WARN_MS = 2600

// Setelah reload, modal pasti tertutup: step "di dalam modal" dipulihkan ke step field-nya.
const RESTORE_MAP = {
  [S.CONTACT_MODAL]: S.CONTACT_FIELD,
  [S.PRODUCT_CATEGORY]: S.PRODUCT_FIELD,
  [S.PRODUCT_MODAL]: S.PRODUCT_FIELD,
  [S.WA_SEND]: S.BTN_SEND,
}

// Step di dalam form tagihan. Dilanjutkan dari halaman lain berarti form sudah kosong lagi,
// jadi panduan mulai lagi dari kolom kontak.
const FORM_STEPS = [S.CONTACT_FIELD, S.PRODUCT_FIELD, S.BTN_SAVE]
const INVOICE_ADD_PATH = '/sales/invoices/add'

// Step "utama" yang menandai milestone backend tercapai → ditulis ke option finance_onboarding.
// profit_loss_viewed TIDAK di sini: ditulis saat FinishModal ditutup (handleClose) supaya
// container tidak keburu unmount (gate) sebelum modal selesai tampil.
const STEP_TO_BE_MILESTONE = {
  [S.BTN_SEND]: FINANCE_ONBOARDING_BE.INVOICE_CREATED, // tagihan pertama tersimpan
  [S.WA_DONE]: FINANCE_ONBOARDING_BE.WHATSAPP_SENT, // tagihan terkirim via WhatsApp
}

// Titik masuk saat localStorage kosong (mis. device baru) tapi backend sudah lebih maju.
// invoice_created tidak dipetakan: butuh id tagihan (ada di path localStorage), tak tersedia
// lintas device → jatuh ke welcome. whatsapp_sent aman ke laporan Laba Rugi (tanpa id).
const BE_TO_STEP = {
  [FINANCE_ONBOARDING_BE.WHATSAPP_SENT]: S.PL_PANEL,
}

const PROFIT_LOSS_PATH = '/reports/profit-loss'
const INVOICE_DETAIL_PATH = '/sales/invoices/detail'

const openSubmenu = (selector) => {
  const sub = document.querySelector(selector)
  if (sub && !sub.classList.contains('ant-menu-submenu-open')) {
    const title = sub.querySelector('.ant-menu-submenu-title')
    if (title) {
      title.click()
    }
  }
}

// Modal antd teratas yang sedang terbuka. Modal bertingkat (mis. Tambah Kategori di dalam
// modal produk) mendapat z-index lebih tinggi dari antd.
const getTopOpenModal = () => {
  let top = null
  let topZ = -Infinity
  document.querySelectorAll('.ant-modal-wrap').forEach((wrap) => {
    if (wrap.style.display === 'none' || wrap.getBoundingClientRect().width <= 0) {
      return
    }
    const z = Number(window.getComputedStyle(wrap).zIndex) || 0
    if (z >= topZ) {
      top = wrap
      topZ = z
    }
  })
  return top
}

// Container global onboarding pertama kali (dimount di layouts/Main saat onboarding aktif —
// isFinanceOnboardingActive: new/invoice_created/whatsapp_sent). Mengatur: welcome, spotlight
// per step, kunci navigasi, persist step di localStorage, dan tulis milestone ke backend.
const FinanceOnboarding = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { formatMessage } = useIntl()
  const step = useSelector(financeOnboardingStepSelector)
  const snoozed = useSelector(financeOnboardingSnoozedSelector)
  const user = useSelector(userSelector)
  const endpoint = useSelector(selectedEndpointSelector)
  const isMobileView = useSelector(mobileViewSelector)
  const loadedInit = useSelector(loadedInitSelector)
  const financeOnboardingValue = useSelector(financeOnboardingValueSelector)
  const {
    settings: { isMenuCollapsed, isMobileMenuOpen },
    onChangeSettings,
  } = useApp()

  const [targetEl, setTargetEl] = useState(null)
  const [unionEl, setUnionEl] = useState(null)
  const [warn, setWarn] = useState(false)
  const anchorRef = useRef(location.pathname)
  const warnTimer = useRef(null)
  // Sudah pernah berada di halaman yang sesuai step; sebelum itu, redirect paksa (mis. tab
  // baru yang mendarat di dashboard) tidak memunculkan toast peringatan.
  const settledRef = useRef(false)
  // Milestone yang sudah dicoba ditulis ke backend di sesi ini — cegah retry berulang saat
  // editOption gagal & revert (yang mengubah nilai option di redux).
  const sentMilestonesRef = useRef({})
  // Ganti perusahaan: option masih milik perusahaan lama sampai inits selesai (loadedInit
  // false lalu true). Jangan restore di jendela itu.
  const blockRestoreRef = useRef(false)
  const scrolledTargetRef = useRef(null)

  const def = step ? STEPS[step] : null

  // Milestone masih `new` padahal tagihan penjualan sudah ada (dibuat di device lain, import,
  // atau PUT milestone dulu gagal): cek tagihan terbaru sekali setelah inits. Key memuat
  // endpoint supaya hasil selalu milik perusahaan aktif, dan sengaja tidak diawali 'invoice'
  // supaya tidak ikut di-refetch saat tagihan disimpan di tengah panduan. Gagal/timeout/403 =
  // dianggap belum ada tagihan (tetap `new`), tanpa toast.
  const needsInvoiceCheck = loadedInit && financeOnboardingValue === FINANCE_ONBOARDING_BE.NEW
  const { data: latestInvoice, isFetched: invoiceChecked } = useQuery(
    ['financeOnboarding', 'invoiceCheck', endpoint],
    async () => {
      const response = await getFinanceInvoices(
        { page: 1, per_page: 1, sort_by: 'id', order_by: 'desc' },
        { timeout: 8000 },
      )
      return response?.data?.data?.data?.[0] || null
    },
    {
      enabled: needsInvoiceCheck,
      retry: false,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    },
  )

  // Container dilepas (panduan selesai, ganti ke perusahaan tanpa panduan, billing expired):
  // buang step di redux supaya ModalBanner/RealtimePanel/widget sidebar tidak memakai sisa step.
  useEffect(() => {
    return () => {
      dispatch(resetFinanceOnboarding())
    }
  }, [dispatch])

  // Ganti perusahaan: buang step di redux supaya dimuat ulang setelah inits perusahaan baru.
  const prevEndpoint = useRef(endpoint)
  useEffect(() => {
    if (prevEndpoint.current && endpoint && prevEndpoint.current !== endpoint) {
      prevEndpoint.current = endpoint
      blockRestoreRef.current = true
      settledRef.current = false
      sentMilestonesRef.current = {}
      dispatch(resetFinanceOnboarding())
      return
    }
    prevEndpoint.current = endpoint
  }, [endpoint, dispatch])

  useEffect(() => {
    if (!loadedInit) {
      blockRestoreRef.current = false
    }
  }, [loadedInit])

  // Kelompok besar dari inits (finance_onboarding). `new` selalu selamat datang:
  // milestone itu belum naik, jadi refresh tidak boleh lanjut ke form tagihan
  // meski localStorage masih menyimpan step detail. Kelompok yang sudah naik
  // (invoice_created / whatsapp_sent) memakai localStorage hanya bila step-nya
  // masih di kelompok itu; kalau tidak, titik masuk kelompok.
  useEffect(() => {
    if (step || !loadedInit || blockRestoreRef.current) {
      return
    }
    // Tunggu hasil cek tagihan supaya Welcome tidak sempat tampil sebelum milestone naik.
    if (
      needsInvoiceCheck &&
      (!invoiceChecked ||
        (latestInvoice?.id && !sentMilestonesRef.current[FINANCE_ONBOARDING_BE.INVOICE_CREATED]))
    ) {
      return
    }
    const saved = loadStep(endpoint)
    const savedStep = saved?.step
    const isNew = financeOnboardingValue === FINANCE_ONBOARDING_BE.NEW
    const inGroup =
      !isNew &&
      savedStep &&
      STEPS[savedStep] &&
      isStepInFinanceOnboardingMilestone(savedStep, financeOnboardingValue)

    let restored
    if (inGroup) {
      restored = RESTORE_MAP[savedStep] || savedStep
      if (saved?.path) {
        anchorRef.current = saved.path
      }
    } else {
      restored = BE_TO_STEP[financeOnboardingValue] || S.WELCOME
      // Beranda aplikasi adalah /dashboard (`/` hanya Redirect). Jangan kunci ke `/`.
      anchorRef.current = restored === S.PL_PANEL ? PROFIT_LOSS_PATH : '/dashboard'
    }
    // Sedang ditunda ("Nanti saja"): tetap tertunda setelah reload. Posisi terakhirnya
    // dipakai selama masih di kelompok milestone yang sama.
    const snooze = loadSnooze(endpoint)
    if (snooze) {
      if (
        STEPS[snooze.step] &&
        isStepInFinanceOnboardingMilestone(snooze.step, financeOnboardingValue)
      ) {
        restored = snooze.step
        anchorRef.current = snooze.path || anchorRef.current
      }
      dispatch(snoozeFinanceOnboarding(restored))
      return
    }
    dispatch(setFinanceOnboardingStep(restored))
  }, [
    step,
    endpoint,
    dispatch,
    financeOnboardingValue,
    loadedInit,
    needsInvoiceCheck,
    invoiceChecked,
    latestInvoice,
  ])

  // Tagihan sudah ada: naikkan milestone ke invoice_created dan lanjut ke step 2 (Kirim) di
  // tagihan terbaru. Step disimpan dulu, lalu editOption (redux optimistik) memicu ulang efek
  // restore di atas yang memulihkan step + path itu. Sekali per sesi: bila PUT gagal & revert
  // ke `new`, panduan tetap di step Kirim (tagihan memang ada) tanpa mengulang PUT; reload
  // berikutnya mencoba lagi. Hanya sebelum step dipulihkan (panduan
  // yang sedang berjalan menaikkan milestone sendiri), dan tidak di jendela ganti perusahaan
  // (option di redux masih milik perusahaan lama).
  useEffect(() => {
    const milestone = FINANCE_ONBOARDING_BE.INVOICE_CREATED
    if (
      step ||
      !needsInvoiceCheck ||
      !latestInvoice?.id ||
      blockRestoreRef.current ||
      sentMilestonesRef.current[milestone]
    ) {
      return
    }
    sentMilestonesRef.current[milestone] = true
    saveStep(endpoint, S.BTN_SEND, `${INVOICE_DETAIL_PATH}/${latestInvoice.id}`)
    dispatch(editOption({ finance_onboarding: milestone }))
  }, [step, needsInvoiceCheck, latestInvoice, endpoint, dispatch])

  useEffect(() => {
    if (snoozed && step) {
      saveSnooze(endpoint, step, anchorRef.current)
    }
  }, [snoozed, step, endpoint])

  // Tulis milestone ke backend saat masuk step utama (tagihan dibuat / WA terkirim). Pakai
  // editOption: update redux optimistik + PUT /options hanya key yang berubah. Forward-only
  // (cegah mundur saat restore) & sekali per milestone tiap sesi (cegah loop saat revert).
  useEffect(() => {
    const milestone = STEP_TO_BE_MILESTONE[step]
    if (!milestone || sentMilestonesRef.current[milestone]) {
      return
    }
    const currentOrder = FINANCE_ONBOARDING_BE_ORDER[financeOnboardingValue] ?? 0
    if (FINANCE_ONBOARDING_BE_ORDER[milestone] > currentOrder) {
      sentMilestonesRef.current[milestone] = true
      dispatch(editOption({ finance_onboarding: milestone }))
    }
  }, [step, financeOnboardingValue, dispatch])

  // Anchor = halaman tempat step berada; disimpan bersama step supaya bisa dipulihkan di
  // tab baru. Anchor diperbarui saat transisi route (efek di bawah) dan transisi tombol
  // (handleStart / handleWaNext); transisi event antar-step di halaman yang sama tidak
  // mengubah anchor.
  useEffect(() => {
    if (step) {
      saveStep(endpoint, step, anchorRef.current)
    }
  }, [step, endpoint])

  const showWarn = useCallback(() => {
    setWarn(true)
    window.clearTimeout(warnTimer.current)
    warnTimer.current = window.setTimeout(() => setWarn(false), WARN_MS)
  }, [])

  // Dilanjutkan dari widget sidebar: kembali ke halaman & step terakhir. Harus dideklarasikan
  // sebelum efek kunci navigasi supaya anchor sudah baru saat efek itu berjalan.
  const prevSnoozedRef = useRef(snoozed)
  const resumingRef = useRef(false)
  useEffect(() => {
    const wasSnoozed = prevSnoozedRef.current
    prevSnoozedRef.current = snoozed
    if (!wasSnoozed || snoozed || !step) {
      return
    }
    clearSnooze(endpoint)
    let next = RESTORE_MAP[step] || step
    let path = anchorRef.current
    if (FORM_STEPS.includes(next) && location.pathname !== path) {
      next = S.CONTACT_FIELD
      path = INVOICE_ADD_PATH
    }
    anchorRef.current = path
    resumingRef.current = true
    if (location.pathname !== path) {
      history.push(path)
    }
    if (next !== step) {
      dispatch(setFinanceOnboardingStep(next))
    }
  }, [snoozed, step, endpoint, location.pathname, history, dispatch])

  // Transisi berbasis route + kunci navigasi.
  useEffect(() => {
    if (!def || snoozed) {
      return
    }
    // Commit yang sama dengan lanjut dari widget: halaman & step di sini masih yang lama
    // (mis. detail tagihan lain yang cocok dengan `next` step Simpan). Tunggu render berikut.
    // Pindah halaman karena lanjut juga bukan pelanggaran, jadi tanpa peringatan.
    if (resumingRef.current) {
      resumingRef.current = false
      settledRef.current = location.pathname === anchorRef.current
      return
    }
    const path = location.pathname
    const next = (def.next || []).find((n) => n.path.test(path))
    if (next) {
      anchorRef.current = path
      dispatch(setFinanceOnboardingStep(next.step))
      return
    }
    // Halaman ini memang milik step (cocok allow) → jadikan anchor URL konkret saat ini,
    // supaya path tersimpan untuk restore selalu akurat (mis. id faktur di halaman detail),
    // walau step sempat berpindah lewat event (kasus "Simpan & Kirim").
    const onAllowedPage = (def.allow || []).some((r) => r.test(path))
    if (onAllowedPage || path === anchorRef.current) {
      settledRef.current = true
      if (onAllowedPage) {
        anchorRef.current = path
      }
      return
    }
    history.replace(anchorRef.current)
    // Hanya peringatkan bila user aktif pindah halaman, bukan saat pengarahan awal restore.
    if (settledRef.current) {
      showWarn()
    }
  }, [location.pathname, def, snoozed, dispatch, history, showWarn])

  // Cari elemen target (mount-nya asinkron), buka submenu bila perlu.
  useEffect(() => {
    if (!def || !def.target || snoozed) {
      setTargetEl(null)
      setUnionEl(null)
      return undefined
    }
    let timer
    const find = () => {
      // Di HP menu ada di drawer. Buka saat step menyorot menu, tutup lagi saat
      // step pindah ke halaman supaya drawer tidak menutupi form.
      if (isMobileView) {
        const needsMenu = Boolean(def.openSubmenu) || String(def.target).includes('kledo-menu')
        if (needsMenu !== isMobileMenuOpen) {
          onChangeSettings({ isMobileMenuOpen: needsMenu })
        }
      }
      if (def.openSubmenu) {
        if (isMenuCollapsed) {
          onChangeSettings({ isMenuCollapsed: false })
        }
        openSubmenu(def.openSubmenu)
      }
      let el = document.querySelector(def.target)
      if (!el && def.fallbackTarget) {
        el = document.querySelector(def.fallbackTarget)
      }
      if (el && def.targetClosest) {
        el = el.closest(def.targetClosest) || el
      }
      // Abaikan target yang masih di luar layar (drawer menu belum selesai terbuka).
      // Kalau target ada tapi di luar layar atau tertutup header/panel langkah, gulung
      // sekali supaya fokusnya kelihatan. Berlaku di semua lebar: halaman detail tagihan
      // bisa terbuka dalam posisi tergulir (sisa gulungan form), tombol Simpan ada di
      // bawah layar laptop.
      // Ada modal lain di atas target (Tambah Kategori, atau modal yang terbuka sebelum
      // step sempat pindah): jeda sorotan. Kalau tidak, mask mengunci seluruh modal itu.
      const topModal = el ? getTopOpenModal() : null
      if (topModal && !topModal.contains(el)) {
        el = null
      }
      if (el) {
        const box = el.getBoundingClientRect()
        if (box.width <= 0 || box.right < 8 || box.left > window.innerWidth - 8) {
          el = null
        } else if (scrolledTargetRef.current !== el) {
          const bottomLimit = window.innerHeight - (window.innerWidth < 768 ? 96 : 16)
          if (box.top < 72 || box.bottom > bottomLimit) {
            el.scrollIntoView({ block: 'center', inline: 'nearest' })
            scrolledTargetRef.current = el
          }
        }
      }
      setTargetEl(el || null)
      setUnionEl(def.union ? document.querySelector(def.union) : null)
      timer = window.setTimeout(find, POLL_MS)
    }
    // Jangan panggil langsung di efek: klik submenu (openSubmenu) memicu flushSync di rc-menu,
    // yang tidak boleh dijalankan saat React masih di lifecycle.
    timer = window.setTimeout(find, 0)
    return () => window.clearTimeout(timer)
  }, [def, snoozed, isMenuCollapsed, isMobileMenuOpen, isMobileView, onChangeSettings])

  useEffect(() => {
    return () => window.clearTimeout(warnTimer.current)
  }, [])

  const handleStart = useCallback(() => {
    anchorRef.current = location.pathname
    dispatch(setFinanceOnboardingStep(S.MENU_INVOICES))
  }, [dispatch, location.pathname])

  // Langsung ke laporan Laba Rugi. Pindah route dulu baru step, supaya kunci navigasi
  // tidak sempat menganggapnya pindah halaman terlarang (toast peringatan).
  const handleWaNext = useCallback(() => {
    anchorRef.current = PROFIT_LOSS_PATH
    history.push(PROFIT_LOSS_PATH)
    dispatch(setFinanceOnboardingStep(S.PL_PANEL))
  }, [dispatch, history])

  // Selesai: tulis milestone akhir profit_loss_viewed ke backend (PUT /options lewat
  // editOption, hanya key yang berubah), bersihkan storage. Milestone ini ditulis di sini
  // (bukan saat step DONE) supaya container tidak unmount sebelum FinishModal ditutup.
  // "Nanti saja": step & halaman terakhir disimpan (efek saveSnooze), panduan disembunyikan
  // sampai dilanjutkan lewat widget "Panduan Memulai" di sidebar.
  const handleSnooze = useCallback(() => {
    dispatch(snoozeFinanceOnboarding())
  }, [dispatch])

  const handleClose = useCallback(() => {
    clearStep(endpoint)
    clearSnooze(endpoint)
    dispatch(editOption({ finance_onboarding: FINANCE_ONBOARDING_BE.PROFIT_LOSS_VIEWED }))
    dispatch(setFinanceOnboardingStep(null))
  }, [dispatch, endpoint])

  if (!loadedInit || !def || snoozed) {
    return null
  }

  // Nama lengkap, sama dengan yang tampil di menu profil.
  const userName = user?.name || ''
  // Sub-judul kartu: bagian kecil di langkah 1 (Kontak/Produk/Simpan), selain itu nama langkahnya.
  const eyebrow = def.group
    ? formatMessage({ id: 'financeOnboarding.badge' }, { n: def.group })
    : ''
  const sub = def.group
    ? formatMessage({ id: def.subId || `financeOnboarding.rail_step${def.group}` })
    : ''

  return (
    <>
      {step === S.WELCOME && <Welcome name={userName} onStart={handleStart} />}
      {step !== S.WELCOME && step !== S.DONE && <ProgressRail group={def.group} pct={def.pct} />}
      {def.target && targetEl && (
        <Spotlight
          target={targetEl}
          union={unionEl}
          placement={def.placement}
          mask={def.mask}
          group={def.group}
          eyebrow={eyebrow}
          sub={sub}
          title={def.titleId ? formatMessage({ id: def.titleId }) : ''}
          desc={def.descId ? formatMessage({ id: def.descId }) : ''}
          hintId={def.hintId}
          warn={warn}
          onRefuse={showWarn}
          onSnooze={handleSnooze}
          preferAbove={Boolean(def.union)}
        />
      )}
      {step === S.WA_DONE && (
        <FloatingCard lock>
          <div className={styles.sent}>✓</div>
          <h4 style={{ textAlign: 'center' }}>
            <FormattedMessage id="financeOnboarding.wa_done_title" />
          </h4>
          <p style={{ textAlign: 'center', marginTop: 6 }}>
            <FormattedMessage id="financeOnboarding.wa_done_desc" />
          </p>
          <Button type="primary" block onClick={handleWaNext} id="btn-finance-onboarding-wa-next">
            <FormattedMessage id="financeOnboarding.wa_done_next" /> →
          </Button>
          <SnoozeButton onClick={handleSnooze} />
        </FloatingCard>
      )}
      <FinishModal open={step === S.DONE} name={userName} onClose={handleClose} />
    </>
  )
}

// Dimount layouts/Main selama milestone belum selesai; panduan sendiri hanya jalan bila juga
// billing tidak expired & user boleh membuat tagihan (isFinanceOnboardingEnabledSelector).
const FinanceOnboardingGate = () => {
  const enabled = useSelector(isFinanceOnboardingEnabledSelector)
  return enabled ? <FinanceOnboarding /> : null
}

export default FinanceOnboardingGate
